import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { Item } from '@/types/item';
import Table from '@/components/molecules/section/Table';
import Container from '@/components/molecules/section/Container';
import { isValidDate, transformDateString } from '@/utilities/datetime';
import styles from './Refund.module.scss';
import { DISPLAY_EMPTY_VALUE } from '@/shared/constants';
import { GridAction } from '@/components/molecules/section/Grid';
import { Modal } from '@/types/modal';
import ModalView from '@/components/modals/Modal';

const COLUMN_MIN_WIDTH = 180;

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState<Modal | null>(null);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.refund.proofOfRefund').toUpperCase(),
      disabled: !item.refunds?.length || item.refunds.length !== 1 || !isValidDate(item.refunds[0].executedAt),
      onClick: () =>
        setModal({
          kind: 'ItemProofOfRefund',
          onClose: () => setModal(null),
          refundData: {
            refundList: [
              {
                ...item.refunds[0],
                brandName: item.brandName,
                productName: item.productName,
              },
            ],
            paymentMethod: item.buyerPaymentMethod,
            paymentStatus: item.paymentCancelled ? 'cancelled' : null,
          },
        }),
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: t('item.refund.creation_date'),
      valueGetter: (params: GridValueGetterParams) => transformDateString(params.value),
      width: 130,
    },
    { field: 'type', headerName: t('item.refund.type') },
    { field: 'amount', headerName: t('item.refund.amount'), width: 120 },
    { field: 'refundAccount', headerName: t('item.refund.refundAccount'), width: COLUMN_MIN_WIDTH },
    { field: 'comment', headerName: t('item.refund.finance_comment'), width: COLUMN_MIN_WIDTH },
    { field: 'status', headerName: t('item.refund.status') },
    { field: 'internalComment', headerName: t('item.refund.internal_comment'), width: COLUMN_MIN_WIDTH },
    {
      field: 'isApproved',
      headerName: t('item.refund.approved'),
      valueGetter: (params: GridValueGetterParams) => (params.value ? t('common.yes') : t('common.no')),
    },
    {
      field: 'executedAt',
      headerName: t('item.refund.executedAt'),
      valueGetter: (params: GridValueGetterParams) => transformDateString(params.value) || DISPLAY_EMPTY_VALUE,
      width: 130,
    },
  ];

  const getBgColor = (state: number) => {
    switch (state) {
      case 1:
        return styles.green;
      case 2:
        return styles.red;
      case 4:
        return styles.yellow;
      default:
        return '';
    }
  };

  const rows = item.refunds.map((refund, index) => ({
    ...refund,
    refundAccount: refund.refundAccount || DISPLAY_EMPTY_VALUE,
    id: index,
    __className__: getBgColor(refund.state),
  }));

  return (
    <>
      <Container
        title={t('item.refund.title')}
        actions={actions}
        testId="refund_table"
      >
        <Table
          scrollHorizontal
          rows={rows}
          columns={columns}
          loading={false}
          table={{ kind: 'PaginationTable', pageSize: 20, pageSizeOptions: [20, 50, 100] }}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
